import React, { useEffect, useState } from 'react';
import { usePartnerTeamRolesQuery } from '@hooks/requests/queries/use_partner_team_roles_query';
import { useInviteMemberMutation } from '@hooks/requests/queries/use_invite_member_mutation';
import { useToast } from '@hooks/useToast';
import { describeMutationError } from '@hooks/requests/utils/_partner_api_client';
import { Modal, Button, Input, Alert, DropdownFilter } from '../ui';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** "PartnerAdmin" -> "Admin", "AceUpViewer" -> "Viewer". */
const roleLabel = (roleName: string): string => {
  if (roleName.endsWith('Admin')) return 'Admin';
  if (roleName.endsWith('Viewer')) return 'Viewer';
  return roleName;
};

/**
 * Invite member modal (FR-001/002/003/004/005/006/007/022).
 *
 * The three outcomes of a submit — fresh invite, resend over a pending one,
 * already-a-member — are entirely a backend decision (BR-002/003/006); this
 * component only reads which one happened off the response and picks the
 * matching toast copy.
 */
const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');

  const rolesQuery = usePartnerTeamRolesQuery({ enabled: isOpen });
  const inviteMutation = useInviteMemberMutation();
  const { showToast } = useToast();

  const roles = rolesQuery.data ?? [];

  // Default to the Viewer option of the caller's own family (FR-001) once
  // the roles list is available. Re-runs every time the modal opens so a
  // prior selection never leaks into the next invite.
  useEffect(() => {
    if (!isOpen || role || roles.length === 0) return;
    const viewerRole = roles.find((candidate) => candidate.name.endsWith('Viewer'));
    setRole(viewerRole ? viewerRole.name : roles[0].name);
  }, [isOpen, roles, role]);

  const resetForm = () => {
    setEmail('');
    setRole('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isEmailValid = EMAIL_PATTERN.test(email);
  const rolesReady = !rolesQuery.isLoading && !rolesQuery.isError;
  const canSubmit = isEmailValid && Boolean(role) && rolesReady;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');

    try {
      const result = await inviteMutation.mutateAsync({ email, role });
      if (result.alreadyMember) {
        showToast(`${email} already has access.`);
      } else if (result.resent) {
        showToast(`Invitation resent to ${email}`);
      } else {
        showToast(`Invitation sent to ${email}`);
      }
      handleClose();
    } catch (err) {
      setError(describeMutationError(err, { fallback: 'Failed to send the invitation' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite member"
      headerVariant="tinted"
      className="api-keys-modal account-role-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="au-modal-pill-btn--confirm"
            onClick={handleSubmit}
            isLoading={inviteMutation.isPending}
            disabled={!canSubmit}
          >
            Send invite
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="au-modal-context-line">
          They&apos;ll receive an email invitation to join this workspace
        </div>

        <Input
          label="Email"
          type="email"
          placeholder="name@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoFocus
        />

        <div>
          {/* A plain span, not a <label for>: the control below is one of
              three mutually-exclusive elements (FR-022's loading/error/ready
              states), and no static htmlFor/id or nesting check can verify
              a conditional pairing. The actual control gets its accessible
              name from `aria-label` directly instead. */}
          <span className="api-keys-value-label block">Role</span>
          {rolesQuery.isLoading && (
            <p className="account-modal-note" data-testid="invite-role-loading">
              Loading roles…
            </p>
          )}
          {rolesQuery.isError && (
            <p
              className="account-modal-note account-modal-note--error"
              data-testid="invite-role-error"
            >
              Unable to load roles right now.
            </p>
          )}
          {rolesReady && (
            // Same single-select DropdownFilter as the User Analytics "Org" filter.
            <div className="account-role-dropdown" data-testid="role-dropdown">
              <DropdownFilter
                name="role"
                required
                options={roles.map((candidate) => ({
                  label: roleLabel(candidate.name),
                  value: candidate.name,
                }))}
                value={role}
                onChange={(_name, value) => {
                  const next = Array.isArray(value) ? value[0] : value;
                  if (next) setRole(next);
                }}
              />
            </div>
          )}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default InviteModal;
